import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import type { RetroResponse } from "shared/model/retro/retro.response";
import type { TaskResponse } from "shared/model/task/task.response";
import type { UserResponse } from "shared/model/user/user.response";
import { RetroService } from "@/api/Retro.service";
import { TaskService } from "@/api/Task.service";
import { UserService } from "@/api/User.service";
import { Card, CardContent } from "@/components/molecules/card/Card";
import {
  PageCard,
  PageCardContent,
  PageCardHeader,
} from "@/components/molecules/page_card/PageCard";
import { UserAvatar } from "@/components/molecules/user_avatar/UserAvatar";
import Navbar from "@/components/organisms/navbar/Navbar";
import { ResponsivePageLayout } from "@/components/organisms/responsive_page_layout/ResponsivePageLayout";
import { Button } from "@/components/ui/button";

export const RetroSummaryView = () => {
  const { retroId } = useParams<{ retroId: string }>();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<TaskResponse[]>([]);
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [retro, setRetro] = useState<RetroResponse | null>(null);

  const userWithTasks = useMemo(
    () =>
      users.filter(
        (user) => tasks.filter((task) => task.ownerId === user.id).length !== 0,
      ),
    [users],
  );
  const userWithoutTasks = useMemo(
    () =>
      users.filter(
        (user) => tasks.filter((task) => task.ownerId === user.id).length === 0,
      ),
    [users],
  );
  const unassignedTasks = useMemo(
    () => tasks.filter((task) => !task.ownerId),
    [tasks],
  );

  useEffect(() => {
    if (!retroId) return;

    TaskService.getTasksByRetroId(retroId).then((tasks) => {
      setTasks(tasks);
    });

    RetroService.getRetroByRetroId(retroId).then((retro) => {
      setRetro(retro);

      UserService.getUsersByTeamId(retro.team_id).then((users) => {
        setUsers(users);
      });
    });
  }, []);

  return (
    <>
      <Navbar />

      <ResponsivePageLayout>
        <PageCard className="max-w-6xl">
          <PageCardHeader>
            Retro {dayjs(retro?.date).format("DD.MM.YYYY")}
          </PageCardHeader>

          <PageCardContent className={"grow justify-between"}>
            {userWithTasks.map((user) => {
              const userTasks = tasks.filter(
                (task) => task.ownerId === user.id,
              );

              return (
                <div key={user.id} className={"flex flex-col gap-4 w-full"}>
                  <div
                    className={
                      "flex flex-row justify-center items-center gap-4"
                    }
                  >
                    <UserAvatar avatarUrl={user.avatar_link} name={user.nick} />

                    {user.nick}
                  </div>

                  <div
                    className={
                      "flex items-center justify-center gap-4 flex-wrap w-full"
                    }
                  >
                    {userTasks.map((task) => {
                      return (
                        <Card
                          id={task.id}
                          key={task.id}
                          className={"w-[350px]"}
                        >
                          <CardContent text={task.text} />
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {unassignedTasks.length !== 0 && (
              <div className={"flex flex-col gap-4 w-full pt-4"}>
                <div className={"flex flex-row justify-center flex-wrap gap-4"}>
                  Nieprzypisane zadania
                </div>

                <div
                  className={
                    "flex items-center justify-center gap-4 flex-wrap w-full"
                  }
                >
                  {unassignedTasks.map((task) => {
                    return (
                      <Card id={task.id} key={task.id} className={"w-[350px]"}>
                        <CardContent text={task.text} />
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {userWithoutTasks.length !== 0 && (
              <div className={"flex flex-col gap-4 w-full pt-4"}>
                <div className={"flex flex-row justify-center flex-wrap gap-4"}>
                  {userWithoutTasks.map((user) => {
                    return (
                      <div
                        className={
                          "flex flex-row justify-center items-center gap-4"
                        }
                        key={user.id}
                      >
                        <UserAvatar
                          avatarUrl={user.avatar_link}
                          name={user.nick}
                        />

                        {user.nick}
                      </div>
                    );
                  })}
                </div>

                <div className={"self-center"}>Brak zadań</div>
              </div>
            )}

            <Button
              className={"mt-4 self-end"}
              onClick={() => {
                navigate(`/team/${retro?.team_id}/board`);
              }}
            >
              Powrót do listy zadań
            </Button>
          </PageCardContent>
        </PageCard>
      </ResponsivePageLayout>
    </>
  );
};
